import { useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ArrowUp, Square } from "lucide-react";
import { cn } from "@/infrastructure/lib/utils";
import { AttachButton, PendingAttachments } from "./ChatAttachments";
import { composerAction } from "../utils/composerAction";

interface ChatComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  /** Campo e anexos bloqueados (sem workspace ou sem modelo). Com o agente trabalhando, o campo segue livre. */
  disabled: boolean;
  /** Resposta em andamento: com o campo vazio, o envio vira Parar (AB#86888). */
  isResponding: boolean;
  onStop: () => void;
  stopDisabled: boolean;
  /**
   * Tarefas em segundo plano na conversa (AB#86889): sem resposta em andamento e com o campo
   * vazio, o botão também vira Parar — e abre a lista das tarefas para cancelar.
   */
  hasBackgroundTasks?: boolean;
  onStopBackground?: () => void;
  canSend: boolean;
  files: File[];
  onPickFiles: (files: File[]) => void;
  onRemoveFile: (index: number) => void;
  /** `hero` é a caixa maior da tela vazia; `dock` a do rodapé da conversa. */
  variant?: "dock" | "hero";
}

const MAX_HEIGHT = { dock: 200, hero: 280 } as const;

/**
 * A caixa de mensagem do Chat. O + (anexos, AB#87121), o texto e o enviar ficam na
 * altura da primeira linha; o campo cresce para cima com o texto, até um teto, e depois rola.
 * Enviar e Parar usam a cor primária da marca.
 */
export function ChatComposer({
  value,
  onChange,
  onSubmit,
  onKeyDown,
  disabled,
  isResponding,
  onStop,
  stopDisabled,
  hasBackgroundTasks = false,
  onStopBackground,
  canSend,
  files,
  onPickFiles,
  onRemoveFile,
  variant = "dock",
}: ChatComposerProps) {
  const { t } = useTranslation();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const hero = variant === "hero";
  // Como no Claude Code (task 87271): com o agente trabalhando, Parar; ao digitar, Enviar — a
  // mensagem entra no que o agente está fazendo. Depois de enviar ou limpar, o Parar volta.
  const action = composerAction({
    typing: value.trim().length > 0 || files.length > 0,
    isResponding,
    hasBackgroundTasks: hasBackgroundTasks && !!onStopBackground,
  });
  const showStop = action !== "send";
  const stop = action === "stop-turn" ? onStop : onStopBackground;

  // Cresce com o conteúdo: zera a altura para medir e aplica o scrollHeight até o teto.
  useLayoutEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    const next = Math.min(el.scrollHeight, MAX_HEIGHT[variant]);
    el.style.height = `${next}px`;
    el.style.overflowY =
      el.scrollHeight > MAX_HEIGHT[variant] ? "auto" : "hidden";
  }, [value, variant]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
      className={cn(
        "rounded-2xl border border-border/70 bg-background shadow-sm transition-colors",
        "focus-within:border-primary/50 focus-within:ring-2 focus-within:ring-primary/10",
        hero ? "p-3" : "p-2",
      )}
    >
      {/* + à esquerda e enviar à direita, no topo; no meio, os anexos e o texto embaixo deles. */}
      <div className="flex items-start gap-2">
        <AttachButton
          onPick={onPickFiles}
          disabled={disabled}
          className={cn(
            "shrink-0 rounded-full",
            hero ? "h-10 w-10" : "h-9 w-9",
          )}
        />
        <div className="flex min-w-0 flex-1 flex-col">
          {files.length > 0 && (
            <div className={hero ? "pt-1" : "pt-0.5"}>
              <PendingAttachments
                files={files}
                onRemove={onRemoveFile}
                disabled={isResponding}
              />
            </div>
          )}
          <textarea
            ref={textareaRef}
            rows={1}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={onKeyDown}
            data-tour="chat-input"
            placeholder={t("playground.view.inputPlaceholder")}
            disabled={disabled}
            className={cn(
              "w-full resize-none border-0 bg-transparent text-foreground placeholder:text-muted-foreground/70",
              "focus:outline-none focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-60",
              // Na tela inicial a caixa já mostra ~3 linhas: deixa claro que cabe mais que uma.
              hero
                ? "py-2.5 text-base leading-6 min-h-[96px]"
                : "py-2 text-sm leading-5 min-h-[36px]",
            )}
          />
        </div>
        {showStop ? (
          <button
            type="button"
            onClick={stop}
            disabled={isResponding && stopDisabled}
            title={t("playground.chat.stop")}
            className={cn(
              "flex shrink-0 items-center justify-center rounded-full border border-primary/30 bg-primary/10 text-primary",
              "transition-colors hover:bg-primary/15 disabled:opacity-40",
              hero ? "h-10 w-10" : "h-9 w-9",
            )}
          >
            <Square className="h-3.5 w-3.5 fill-current" />
            <span className="sr-only">{t("playground.chat.stop")}</span>
          </button>
        ) : (
          <button
            type="submit"
            disabled={!canSend}
            title={t("playground.chat.send")}
            className={cn(
              "flex shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-sm",
              "transition-colors hover:bg-primary/90",
              "disabled:bg-muted disabled:text-muted-foreground disabled:shadow-none",
              hero ? "h-10 w-10" : "h-9 w-9",
            )}
          >
            <ArrowUp
              className={hero ? "h-5 w-5" : "h-4 w-4"}
              strokeWidth={2.25}
            />
            <span className="sr-only">{t("playground.chat.send")}</span>
          </button>
        )}
      </div>
    </form>
  );
}
